import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import _ from 'lodash';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, forkJoin, map, Observable, of } from 'rxjs';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { SelectItem } from 'primeng/api';
import { BreadcrumbService, EnumValues } from '@card-software/library';
import { environment } from '../../../environments/environment';
import { Asset } from '../../models/Asset';
import { AssetCategory } from '../../models/AssetCategory';
import { AssetType } from '../../models/AssetType';
import { AssetModel } from '../../models/AssetModel';
import { AssetModelProperty } from '../../models/AssetModelProperty';
import { AssetPrefix } from '../../models/AssetPrefix';
import { Process } from '../../models/Process';
import { Role } from '../../models/user-master/role';
import { Site } from '../../models/Site';
import { AssetProxyService } from '../../proxies/asset/asset.proxy.service';
import { CategoryProxyService } from '../../proxies/asset/category.proxy.service';
import { TypeProxyService } from '../../proxies/asset/types.proxy.service';
import { ModelProxyService } from '../../proxies/asset/model.proxy.service';
import { ModelPropertyProxyService } from '../../proxies/asset/model-property.proxy.service';
import { PrefixProxyService } from '../../proxies/asset/prefix.proxy.service';
import { ProcessProxyService } from '../../proxies/item/process.proxy.service';
import { SiteProxyService } from '../../proxies/asset/site.proxy.service';
import { RoleProxyService } from '../../proxies/user-master/role.proxy.service';
import { EnumService } from '../../services/enum.service';
import { LocalHelperService } from '../../services/local-helper.service';

const MAX_WEIGHT_STATION_ROLES = 5;

interface WeightStationDetailsForm {
  area: FormControl<string | null>;
  accessibleRoles: FormControl<string[]>;
}

@Component({
  selector: 'app-asset-details',
  templateUrl: './asset-details.component.html',
  styleUrls: ['./asset-details.component.scss'],
})
export class AssetDetailsComponent implements OnInit {
  private readonly assetProxyService = inject(AssetProxyService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly categoryProxyService = inject(CategoryProxyService);
  private readonly siteProxyService = inject(SiteProxyService);
  private readonly typeProxyService = inject(TypeProxyService);
  private readonly modelProxyService = inject(ModelProxyService);
  private readonly modelPropertyProxyService = inject(ModelPropertyProxyService);
  private readonly prefixProxyService = inject(PrefixProxyService);
  private readonly enumService = inject(EnumService);
  private readonly processProxyService = inject(ProcessProxyService);
  private readonly roleProxyService = inject(RoleProxyService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  readonly localeHelperService = inject(LocalHelperService);

  breadCrumsItems = [{ label: 'Assets', route: '/assets' }, { label: 'Asset Detail' }];

  asset: Asset | null = null;
  isLoading = signal(0);
  isEditing = false;
  isSaving = false;

  get showKnittingMachineDetails(): boolean {
    return this.assetCategory.value?.id === environment.knittingMachineCategoryId;
  }

  get isDoubleKnit(): boolean {
    return this.assetType.value?.id === environment.doubleKnitAssetTypeId;
  }

  get showDyeingMachineDetails(): boolean {
    return this.assetCategory.value?.id === environment.dyeingMachineCategoryId;
  }

  get isScanningStation(): boolean {
    return this.assetType.value?.assetPrefix?.prefix === 'SST';
  }

  get isWeightStation(): boolean {
    return this.prefix.value?.prefix === 'WST';
  }

  get showCapacitySection(): boolean {
    return (this.showDyeingMachineDetails || this.showKnittingMachineDetails) && !this.isScanningStation;
  }

  categories: AssetCategory[] = [];
  types: AssetType[] = [];
  sites: Site[] = [];
  prefixes: AssetPrefix[] = [];

  filteredTypes: AssetType[] = [];
  models: AssetModel[] = [];
  filteredModels: AssetModel[] = [];
  propertyModels: AssetModelProperty[] = [];
  filteredPropertyModels: AssetModelProperty[] = [];

  editingProperty: AssetModelProperty | null = null;
  addValues: string[] = [];
  newValue: FormControl<string | null> = new FormControl('');
  disableAddButton = false;

  // Weight Station area and role access
  readonly maxWeightStationRoles = MAX_WEIGHT_STATION_ROLES;
  roles = signal<Role[]>([]);
  weightStationDetails = new FormGroup<WeightStationDetailsForm>({
    area: new FormControl<string | null>(null),
    accessibleRoles: new FormControl<string[]>([], {
      nonNullable: true,
      validators: [Validators.maxLength(MAX_WEIGHT_STATION_ROLES)],
    }),
  });

  // Scanning Station process selection
  processes: Process[] = [];
  selectedProcesses: Process[] = [];
  processSearchQuery: FormControl<string | null> = new FormControl('');

  assetId: FormControl<string | null> = new FormControl('');
  prefix: FormControl<AssetPrefix | null> = new FormControl(null, [Validators.required]);
  description: FormControl<string | null> = new FormControl('');

  assetCategory: FormControl<AssetCategory | null> = new FormControl(null, [Validators.required]);
  assetType: FormControl<AssetType | null> = new FormControl(null, [Validators.required]);
  assetModel: FormControl<AssetModel | null> = new FormControl(null);

  siteReference: FormControl<string | null> = new FormControl('', [Validators.required]);
  assetManufacturerReference: FormControl<string | null> = new FormControl('');
  createdBy: FormControl<string | null> = new FormControl('');
  modifiedBy: FormControl<string | null> = new FormControl('');
  modifiedDate: FormControl<Date> = new FormControl(new Date(), {
    nonNullable: true,
  });

  // Machine form controls
  diameterInches = new FormControl(0);
  gauge = new FormControl(0);
  feeders = new FormControl(0);
  cylinderNeedles = new FormControl(0);
  dialNeedles = new FormControl(0);

  // Dyeing machine form controls
  numberOfPorts = new FormControl(0);
  portCapacity = new FormControl(0);

  //Capacity form controls
  maxCapacity = new FormControl(0);
  minCapacity = new FormControl(0);
  capacityUOM = new FormControl(null);

  knittingMachineDetails: FormGroup = new FormGroup({
    diameterInches: this.diameterInches,
    gauge: this.gauge,
    feeders: this.feeders,
    cylinderNeedles: this.cylinderNeedles,
    dialNeedles: this.dialNeedles,
  });

  dyeingMachineDetails: FormGroup = new FormGroup({
    numberOfPorts: this.numberOfPorts,
    portCapacity: this.portCapacity,
  });

  assetCapacity: FormGroup = new FormGroup({
    maxCapacity: this.maxCapacity,
    minCapacity: this.minCapacity,
    capacityUOM: this.capacityUOM,
  });

  formAssetGroup: FormGroup = new FormGroup({
    assetId: this.assetId,
    prefix: this.prefix,
    description: this.description,
    assetCategory: this.assetCategory,
    assetType: this.assetType,
    assetModel: this.assetModel,
    siteReference: this.siteReference,
    assetManufacturerReference: this.assetManufacturerReference,
    createdBy: this.createdBy,
    modifiedBy: this.modifiedBy,
    modifiedDate: this.modifiedDate,
    knittingMachineDetails: this.knittingMachineDetails,
    dyeingMachineDetails: this.dyeingMachineDetails,
    assetCapacity: this.assetCapacity,
  });

  capacityUOMSelectOptions: SelectItem<number>[];

  constructor() {
    this.capacityUOMSelectOptions = this.toPrimeSelectOptions(this.enumService.getEnum('CapacityUOM').values);
  }

  ngOnInit(): void {
    this.increaseLoading();

    this.loadInitialData();

    this.assetCategory.valueChanges.subscribe((reference) => {
      if (this.asset) {
        this.asset.assetModelParameters = [];
      }

      this.filteredPropertyModels = [];
      this.editingProperty = null;
      this.assetType.setValue(null);
      this.assetModel.setValue(null);
      this.filteredTypes = [];
      this.filteredModels = [];
      this.filterModels = [];

      if (reference) {
        if (reference.id === environment.knittingMachineCategoryId) {
          this.prefix.setValue(this.prefixes.find((prefix) => prefix.prefix === 'KM') || null, { emitEvent: false });
        }
        if (reference.id === environment.dyeingMachineCategoryId) {
          this.prefix.setValue(this.prefixes.find((prefix) => prefix.prefix === 'DM') || null, { emitEvent: false });
        }
        if (reference.id === environment.stationsCategoryId) {
          this.prefix.setValue(this.prefixes.find((prefix) => prefix.prefix === 'WST') || null, { emitEvent: false });
        }
        this.populateTypes();
      } else {
        this.prefix.setValue(null, { emitEvent: false });
      }
    });

    this.assetType.valueChanges.subscribe((reference) => {
      if (this.asset) {
        this.asset.assetModelParameters = [];
      }
      this.filteredPropertyModels = [];
      this.editingProperty = null;
      // Clear selected processes when changing asset type
      this.selectedProcesses = [];
      this.assetModel.setValue(null);
      this.filteredModels = [];
      this.filterModels = [];

      if (reference) {
        // Set prefix from AssetType if available
        if (reference.assetPrefix) {
          this.prefix.setValue(this.prefixes.find((p) => p.id === reference.assetPrefixReference) || reference.assetPrefix, {
            emitEvent: false,
          });
        }
        this.populateModels();
      }
    });

    this.assetModel.valueChanges.subscribe((model) => {
      if (model) {
        this.editingProperty = null;
        if (this.asset) {
          this.asset.assetModelParameters = [];
        }
        this.populateModelProperties(model.id);
      } else {
        this.filteredPropertyModels = [];
        this.editingProperty = null;
        if (this.asset) {
          this.asset.assetModelParameters = [];
        }
      }
    });
  }

  async loadInitialData() {
    forkJoin([
      this.categoryProxyService.getCategories(),
      this.siteProxyService.getSites(),
      this.typeProxyService.getTypes(),
      this.modelProxyService.getModels(),
      this.prefixProxyService.getPrefixes(),
    ])
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe(([categories, sites, types, models, prefixes]) => {
        if (categories.data) {
          this.categories = categories.data;
        }
        if (sites.data) {
          this.sites = sites.data;
        }
        if (types.data) {
          this.types = types.data;
        }
        if (models.data) {
          this.models = models.data;
        }
        if (prefixes.data) {
          this.prefixes = prefixes.data;
        }
        this.getAsset();
      });

    // Loaded on its own so a roles failure doesn't block the asset form
    this.roleProxyService.getAllRoles().subscribe({
      next: (response) => {
        this.roles.set([...(response.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)));
      },
      error: () => this.roles.set([]),
    });
  }

  private toPrimeSelectOptions(value: EnumValues): SelectItem<number>[] {
    return Object.keys(value).map((key) => {
      const numKey = parseInt(key, 10);
      return {
        label: value[numKey],
        value: numKey,
      };
    });
  }

  private increaseLoading(): void {
    this.isLoading.update((count) => count + 1);
  }

  private decreaseLoading(): void {
    this.isLoading.update((count) => Math.max(count - 1, 0));
  }

  getAsset() {
    this.route.queryParams.subscribe((params) => {
      if (params['assetId'] && params['assetId'] !== 'new') {
        this.isEditing = true;
        this.increaseLoading();
        this.assetProxyService
          .getAssetById(params['assetId'])
          .pipe(finalize(() => this.decreaseLoading()))
          .subscribe((response) => {
            this.asset = response.data;
            this.breadcrumbService.setCurrent(this.asset?.assetId);
            const originalParams = this.asset?.assetModelParameters;

            if (this.asset) {
              this.formAssetGroup.patchValue(
                {
                  assetId: this.asset.assetId || '',
                  description: this.asset.description || '',
                  prefix: this.prefixes.find((prefix) => prefix.id === this.asset?.prefixReference) || null,
                  siteReference: this.asset.siteReference || '',
                  assetType: this.types.find((type) => type.id === this.asset?.assetTypeReference),
                  assetCategory: this.categories.find((category) => category.id === this.asset?.assetCategoryReference) || null,
                  assetModel: this.models.find((model) => model.id === this.asset?.assetModelReference) || null,
                  assetManufacturerReference: this.asset?.assetManufacturerReference || '',
                  createdBy: this.asset.createdBy || '',
                  modifiedBy: this.asset.modifiedBy || '',
                  modifiedDate: this.asset.modifiedDate || new Date(),
                  knittingMachineDetails: this.asset.knittingMachineDetails,
                  dyeingMachineDetails: this.asset.dyeingMachineDetails,
                  assetCapacity: this.asset.assetCapacity,
                },
                { emitEvent: false },
              );

              if (this.asset.assetModelReference) {
                this.populateModelProperties(this.asset?.assetModelReference);
              }

              this.weightStationDetails.reset({
                area: this.asset.weightStationDetails?.area ?? null,
                accessibleRoles: this.asset.weightStationDetails?.accessibleRoles ?? [],
              });

              // Load scanning station details if present
              if (this.asset.scanningStationDetails?.processIds?.length) {
                this.loadProcessesByIds(this.asset.scanningStationDetails.processIds);
              }

              this.populateTypes();
              this.populateModels();
              this.asset.assetModelParameters = originalParams!;
            }
          });
      } else {
        this.breadcrumbService.setCurrent('Common_New');
        this.asset = {
          id: '',
          assetId: '',
          name: '',
          description: '',
          assetCategoryReference: '',
          siteReference: '',
          assetTypeReference: '',
          prefixReference: '',
          assetModelReference: '',
          assetManufacturerReference: '',
          assetModelParameters: [],
          quantity: 1,
          createdBy: '',
          modifiedBy: '',
          modifiedDate: new Date(),
        };
      }
    });
  }

  populateTypes() {
    this.filteredTypes = this.types.filter((type) => type.assetCategoryReference === this.assetCategory?.value?.id);
  }

  populateModels() {
    this.filteredModels = this.models.filter((model) => model.assetType?.id === this.assetType?.value?.id);
  }

  populateModelProperties(modelId: string): void {
    const selectedModel = this.models.find((model) => model.id === modelId);

    if (selectedModel && (selectedModel.modelProperties?.length ?? 0) > 0) {
      this.increaseLoading();
      this.modelPropertyProxyService
        .getMultipleModelProperties(selectedModel.modelProperties?.map((property) => property.id) ?? [])
        .pipe(finalize(() => this.decreaseLoading()))
        .subscribe({
          next: (response) => {
            this.filteredPropertyModels = _.map(response.data, (property) => {
              property.defaultValues =
                this.asset?.assetModelParameters.find((param) => param.id === property.id)?.values ?? property.defaultValues;
              return property;
            });
          },
          error: () => {
            this.filteredPropertyModels = [];
          },
        });
    } else {
      this.filteredPropertyModels = [];
    }
  }

  enableEditing(property: AssetModelProperty): void {
    if (property.setValuesAtAssetLevel) {
      this.editingProperty = property;
      this.addValues = property.defaultValues;
    }
  }

  addValueToDefaultValues(): void {
    if (!this.newValue.value) {
      console.warn('Input value is empty');
      return;
    }

    if (this.editingProperty?.multipleValues) {
      this.addValues.push(this.newValue.value.trim());

      this.newValue.setValue('');
    } else {
      if (this.addValues.length >= 1) {
        this.disableAddButton = true;
      } else {
        this.addValues.push(this.newValue.value.trim());
        this.newValue.setValue('');
      }
    }

    this.newValue.setValue('');
  }

  onCancelEdit(): void {
    this.clearEditingState();
  }

  removeValueFromDefaultValues(index: number): void {
    this.addValues.splice(index, 1);
  }

  getValues(values: string[]): string {
    return values.join(', ');
  }

  onSaveValueProperty(property: AssetModelProperty): void {
    this.filteredPropertyModels.forEach((propertyModel) => {
      if (propertyModel.id === property.id) {
        propertyModel.defaultValues.push(...(this.newValue.value ? [this.newValue.value] : []));
      }
    });
    this.newValue.setValue('');
  }

  clearEditingState(): void {
    this.editingProperty = null;
    this.addValues = [];
    this.newValue.setValue('');
  }

  // #region typeaheads
  filteredCategories: AssetCategory[] = [];
  categoryTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    this.filteredCategories = this.categories
      .filter((t) => t.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8);
  }

  typeTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    const filteredTypes = this.types.filter((t) => t.assetCategoryReference === this.assetCategory.value?.id);
    this.filteredTypes = filteredTypes
      .filter((t) => t.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8);
    if (!this.filteredTypes) this.assetType.setValue(null);
  }

  filterModels: AssetModel[] = [];
  modelTypeahead(event: AutoCompleteCompleteEvent) {
    const query = event.query.trim().toLowerCase();
    const filteredModels = this.models.filter((t) => t.assetType?.id === this.assetType.value?.id);
    this.filterModels = filteredModels
      .filter((t) => t.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 8);
  }

  prefixTypeahead = (term: string): Observable<AssetPrefix[]> => {
    term = term.trim().toLowerCase();
    if (!term) {
      return of(this.prefixes);
    }

    return of(this.prefixes.filter((prefix) => prefix.prefix.toLowerCase().includes(term)));
  };

  // #region typeaheads

  // #region Process Search (for Scanning Stations)
  public fetchProcesses = (query: string): Observable<Process[]> =>
    this.processProxyService.searchProcesses({ processName: query }).pipe(
      // Filter out already selected processes and add displayName
      map((response) =>
        (response.data ?? [])
          .filter((p) => !this.selectedProcesses.some((sp) => sp.id === p.id))
          .map((p) => ({
            ...p,
            displayName: this.localeHelperService.getLocalizedValue(p.name),
          })),
      ),
    );

  addProcess(event: AutoCompleteSelectEvent): void {
    const process: Process = event.value;

    if (!this.selectedProcesses.some((p) => p.id === process.id)) {
      this.selectedProcesses = [...this.selectedProcesses, process];
    }
    this.processSearchQuery.setValue('');
  }

  removeProcess(process: Process): void {
    this.selectedProcesses = this.selectedProcesses.filter((p) => p.id !== process.id);
  }

  loadProcessesByIds(processIds: string[]): void {
    // Search for each process ID and add to selected processes
    this.increaseLoading();
    this.processProxyService
      .searchProcesses({})
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.selectedProcesses = response.data
              .filter((p) => processIds.includes(p.id))
              .map((p) => ({
                ...p,
                displayName: this.localeHelperService.getLocalizedValue(p.name),
              }));
          }
        },
      });
  }
  // #endregion

  onAddNewType() {
    this.router.navigate(['/admin/assets-details'], {
      queryParams: {
        addNewType: true,
        categoryReference: this.assetCategory.value?.id,
      },
    });
  }

  onAddNewModel() {
    this.router.navigate(['/admin/assets-details'], {
      queryParams: {
        addNewModel: true,
        typeReference: this.assetType.value?.id,
      },
    });
  }

  isButtonDisabled(): boolean {
    const baseInvalid =
      this.formAssetGroup.invalid ||
      typeof this.assetType.value === 'string' ||
      typeof this.assetCategory.value === 'string' ||
      typeof this.assetModel.value === 'string' ||
      (this.isWeightStation && this.weightStationDetails.invalid);

    return baseInvalid || this.isSaving;
  }

  onSave() {
    if (this.formAssetGroup.valid && !this.isSaving) {
      const currentDate = new Date();
      this.modifiedDate.setValue(currentDate);

      const asset: Asset = {
        id: this.asset?.id || '',
        assetId: this.assetId.value || '',
        name: this.asset?.name || this.description.value || '',
        prefixReference: this.prefix.value?.id || '',
        description: this.description.value || '',
        quantity: this.asset?.quantity ?? 1,
        assetCategoryReference: this.assetCategory.value?.id || '',
        siteReference: this.siteReference.value || '',
        assetTypeReference: this.assetType.value?.id || '',
        assetModelReference: this.assetModel.value?.id || null,
        assetManufacturerReference: this.assetManufacturerReference.value || null,
        assetModelParameters: this.filteredPropertyModels
          .filter((p) => p.setValuesAtAssetLevel)
          .map((property) => ({
            id: property.id,
            values: property.defaultValues,
          })),
        knittingMachineDetails: this.showKnittingMachineDetails ? this.knittingMachineDetails.value : undefined,
        dyeingMachineDetails: this.showDyeingMachineDetails ? this.dyeingMachineDetails.value : undefined,
        assetCapacity: this.showCapacitySection ? this.assetCapacity.value : undefined,
        scanningStationDetails: this.isScanningStation
          ? {
              processIds: this.selectedProcesses.map((p) => p.processId.toString()),
              processNames: this.selectedProcesses.map((p) => ({
                processId: p.processId.toString(),
                processName: p.name,
              })),
            }
          : undefined,
        // The upsert replaces the whole asset, so the existing weight topic must be sent back
        weightStationDetails: this.isWeightStation
          ? {
              weightTopic: this.asset?.weightStationDetails?.weightTopic ?? null,
              area: this.weightStationDetails.controls.area.value?.trim() || null,
              accessibleRoles: this.weightStationDetails.controls.accessibleRoles.value,
            }
          : undefined,
        createdBy: this.createdBy.value || '',
        modifiedBy: this.modifiedBy.value || '',
        modifiedDate: currentDate,
      };

      this.isSaving = true;
      this.assetProxyService
        .upsertAsset(asset)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe(() => {
          this.router.navigate(['/assets']);
        });
    }
  }

  onClose() {
    this.router.navigate(['/assets']);
  }
}
